<template>
  <main class="change-password">
    <!-- Header -->
    <div class="change-password__header">
      <div class="change-password__header-content">
        <h1 class="change-password__title">Change Password</h1>
        <p class="change-password__subtitle">Update your account password</p>
      </div>
    </div>

    <!-- Content -->
    <div class="change-password__content">
      <form @submit.prevent="submitData" class="change-password__form">
        <div class="change-password__form-group">
          <label class="change-password__label">New Password</label>
          <SecurityPasswordInput
            placeHolder="Enter new password"
            label="current password"
            :password="payload.password"
            :error="newPasswordError || {}"
            inputName="newPassword"
            :disabled="loading"
            @set="setNewPassword"
            @validate="validateMethod"
          />
        </div>

        <!-- Password Requirements -->
        <div class="change-password__requirements">
          <div class="change-password__requirement" :class="{ 'is-valid': passwordLength }">
            <svg v-if="passwordLength" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13.333 4L5.99967 11.3333L2.66634 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="3" fill="currentColor"/>
            </svg>
            <span>At least 8 characters</span>
          </div>
          <div class="change-password__requirement" :class="{ 'is-valid': containsNumber }">
            <svg v-if="containsNumber" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13.333 4L5.99967 11.3333L2.66634 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="3" fill="currentColor"/>
            </svg>
            <span>At least one number</span>
          </div>
          <div class="change-password__requirement" :class="{ 'is-valid': containsUppercase }">
            <svg v-if="containsUppercase" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13.333 4L5.99967 11.3333L2.66634 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="3" fill="currentColor"/>
            </svg>
            <span>At least one uppercase letter</span>
          </div>
          <div class="change-password__requirement" :class="{ 'is-valid': containsSpecial }">
            <svg v-if="containsSpecial" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13.333 4L5.99967 11.3333L2.66634 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="3" fill="currentColor"/>
            </svg>
            <span>At least one special character</span>
          </div>
        </div>

        <div class="change-password__form-group">
          <label class="change-password__label">Confirm Password</label>
          <SecurityPasswordInput
            placeHolder="Confirm new password"
            label="new password"
            :password="payload.confirmPassword"
            :error="confirmPasswordError || {}"
            inputName="confirmPassword"
            :disabled="enableConfirmPassword"
            @set="setConfirmPassword"
            @validate="validateMethod"
          />
        </div>

        <div class="change-password__actions">
          <button
            type="button"
            class="change-password__cancel-btn"
            @click="$router.push('/dashboard/account')"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="change-password__submit-btn"
            :disabled="loading"
          >
            <span v-if="!loading">Change Password</span>
            <span v-else class="change-password__spinner">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" class="tw-animate-spin">
                <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" stroke-opacity="0.3"/>
                <path d="M10 2C14.4183 2 18 5.58172 18 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </span>
          </button>
        </div>
      </form>
    </div>
  </main>
</template>

<script>
import { mapActions } from "vuex";
import useValidate from "@vuelidate/core";
import AccountSidebar from "@/layout/navigation/AccountSidebar.vue";
import {
  required,
  minLength,
  maxLength,
  helpers,
  sameAs,
} from "@vuelidate/validators";
import SecurityPasswordInput from "@/components/account/SecurityPasswordInput.vue";
import { getItem, clearStorage } from "@/utils/storage";

export default {
  name: "ChangePassword",

  components: { AccountSidebar, SecurityPasswordInput },

  beforeRouteEnter(to, from, next) {
    const loggedIn = getItem("org_details");
    const { token } = to.query;
    if (!token) {
      clearStorage();
      return next({ name: "SignIn" });
    }
    if (loggedIn === null) {
      return next({ name: "ResetPassword", query: { token } });
    }
    next();
  },

  data() {
    return {
      v$: useValidate(),
      loading: false,
      payload: {
        password: "",
        confirmPassword: "",
        token: this.$route.query.token,
      },
      enableConfirmPassword: true,
      newPasswordError: {},
      confirmPasswordError: {},
      containsNumber: false,
      containsSpecial: false,
      containsUppercase: false,
      passwordLength: false,
    };
  },

  validations() {
    return {
      payload: {
        password: {
          required,
          minLength: minLength(8),
          maxLength: maxLength(50),
          containsUppercase: helpers.withMessage(
            "Must contain upper case letter",
            function (value) {
              return /[A-Z]/.test(value);
            }
          ),
          containsNumber: helpers.withMessage(
            "Must contain number",
            function (value) {
              return /[0-9]/.test(value);
            }
          ),
          containsSpecial: helpers.withMessage(
            "Must contain special character [#?!@$%^&*-]",
            function (value) {
              return /[#?!@$%^&*-]/.test(value);
            }
          ),
        },
        confirmPassword: {
          required,
          sameAs: sameAs(this.payload.password),
        },
      },
    };
  },

  methods: {
    ...mapActions("organisationModule", ["resetPassword"]),
    ...mapActions(["showToast"]),

    async submitData() {
      this.v$.$validate();
      const hasValue = Object.keys(this.payload).every(
        (key) => this.payload[key]
      );
      if (this.v$.$errors.length === 0 && hasValue) {
        this.loading = true;
        delete this.payload.confirmPassword;
        const response = await this.resetPassword(this.payload);
        if (response) {
          const { message } = response.data;
          this.showToast({
            description: message,
            display: true,
            type: "success",
          });
          this.$router.push("/dashboard/account");
        }
        this.loading = false;
      }
    },

    setNewPassword(password) {
      this.payload.confirmPassword = "";
      this.payload.password = password;
      if (password.length >= 8) {
        this.enableConfirmPassword = false;
      } else {
        this.enableConfirmPassword = true;
      }
    },

    setConfirmPassword(password) {
      this.payload.confirmPassword = password;
    },

    validateMethod(field) {
      switch (field) {
        case "Enter new password":
          this.v$.$reset();
          this.v$.payload.password.$touch();
          if (this.v$.$errors.length === 0)
            return (this.newPasswordError = {});
          this.newPasswordError = this.v$.$errors[0];
          break;
        case "Confirm new password":
          this.v$.$reset();
          this.v$.payload.confirmPassword.$touch();
          if (this.v$.$errors.length === 0)
            return (this.confirmPasswordError = {});
          this.confirmPasswordError = this.v$.$errors[0];
          break;

        default:
          break;
      }
    },

    checkPassword(password) {
      this.containsNumber = /[0-9]/.test(password);
      this.containsSpecial = /[#?!@$%^&*-]/.test(password);
      this.containsUppercase = /[A-Z]/.test(password);
      this.passwordLength = password.length >= 8;
    },
  },

  watch: {
    newPasswordError(newValue, oldValue) {
      deep: true;
      immediate: true;
      if (newValue !== oldValue) {
        this.checkPassword(this.payload.password);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.change-password {
  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 24px;
    border-bottom: 1px solid #E2E8F0;
  }

  &__header-content {
    flex: 1;
  }

  &__title {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 4px 0;
  }

  &__subtitle {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
  }

  &__content {
    padding: 24px;
    max-width: 480px;
  }

  &__form-group {
    margin-bottom: 20px;
  }

  &__label {
    display: block;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
    margin-bottom: 8px;
  }

  &__requirements {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 24px;
    padding: 16px;
    background: #F8FAFC;
    border-radius: 10px;
  }

  &__requirement {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #94A3B8;
    transition: color 0.15s ease;

    &.is-valid {
      color: #10B981;
    }

    svg {
      flex-shrink: 0;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 32px;
  }

  &__cancel-btn {
    padding: 10px 20px;
    background: white;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #64748B;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: #CBD5E1;
      color: #0F172A;
    }
  }

  &__submit-btn {
    padding: 10px 20px;
    background: #4D84FF;
    border: none;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: white;
    cursor: pointer;
    transition: all 0.15s ease;
    min-width: 140px;

    &:hover:not(:disabled) {
      background: #3B6FE8;
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  &__spinner {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
